import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import {
  type AgentSourceCitation,
  type CitationCellAnchor,
  type FileCitation,
} from "@mg/schemas/src/commons/ticket";

import { formatTimeStamp } from "../../../routes/tickets/components/VideoPlayback";
import {
  type CitationVariant,
  type TicketMessageCitation,
  cellAnchorLabel,
  isAgentSourceCitation,
  isDrawerRenderableVariant,
  isFileCitation,
  isGraphCitation,
  isKnowledgeDbCitation,
  isKnowledgeRuleCitation,
  isNavigableFileCitation,
  isTextCitation,
  resolveCitationVariant,
} from "../../../utils/citations";

// why: shared by the studio comment drawer (routes/tickets), the shared
// KnowledgeRuleCitationDialog (src/components), and this module — so they live
// in src/utils/citations, the sanctioned shared home that src/ask-v2 can also
// reach. The import wall (.eslintrc.json `no-restricted-imports`) only bans
// src/ask-v2/** from importing routes/ask-puntt; nothing stops routes/tickets
// from doing so. Re-exported here so existing ask-puntt imports keep resolving.
//
// The whole set of union PREDICATES moved there with `resolveCitationVariant`:
// the drawer and this module dispatch over the same union, and the resolver has
// to sit beside the predicates it calls (this module imports `formatTimeStamp`
// from routes/tickets, so the dependency cannot point the other way). Labelling
// and de-duplication stay here — `citationLabel` needs that same
// routes/tickets helper.
export {
  type CitationVariant,
  type TicketMessageCitation,
  cellAnchorLabel,
  isAgentSourceCitation,
  isDrawerRenderableVariant,
  isFileCitation,
  isGraphCitation,
  isKnowledgeDbCitation,
  isKnowledgeRuleCitation,
  isNavigableFileCitation,
  isTextCitation,
  resolveCitationVariant,
};

/** Payload for navigating the context pane to a cited source. */
export type CitationTarget = {
  pageNumber?: number;
  position?: { x: number; y: number };
  sourceUrl?: string;
  originalURL?: string;
  videoStart?: number;
  cellAnchor?: CitationCellAnchor;
  knowledgeDataRecordId?: string;
};

/** @deprecated Use `CitationTarget` — kept as alias for in-flight imports. */
export type ContextCitationTarget = CitationTarget;

export type ContextAssetRef = {
  source: string;
  name?: string;
};

export type ContextSourcePin = {
  relX: number;
  relY: number;
  page: number;
  sourceUrl: string;
  originalURL?: string;
  videoStart?: number;
  cellAnchor?: CitationCellAnchor;
};

export type DraftPin = { relX: number; relY: number; page: number };

export type WebSourceCitation = AgentSourceCitation & { source: "web" };

/** A Warhol web citation is navigable only when it carries an absolute HTTP(S)
 * URL. Treat persisted citation data as untrusted here: `sourceId` is a plain
 * string in the shared schema and older rows may predate backend validation. */
export function isWebSourceCitation(
  citation: TicketMessageCitation,
): citation is WebSourceCitation {
  if (
    !("source" in citation) ||
    !("sourceId" in citation) ||
    citation.source !== "web"
  ) {
    return false;
  }

  try {
    const url = new URL(citation.sourceId);
    return (
      (url.protocol === "https:" || url.protocol === "http:") &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
}

/** Web sources shown below a chat answer, with duplicate URLs collapsed. */
export function dedupeWebSourceCitations(
  citations: readonly TicketMessageCitation[],
): WebSourceCitation[] {
  const seen = new Set<string>();
  const out: WebSourceCitation[] = [];
  for (const citation of citations) {
    if (!isWebSourceCitation(citation)) continue;
    const normalizedUrl = new URL(citation.sourceId).href;
    if (seen.has(normalizedUrl)) continue;
    seen.add(normalizedUrl);
    out.push(citation);
  }
  return out;
}

/** Graph citations to render, collapsed by the node they describe. One claim
 * surfaced by two tool calls in the same turn would otherwise draw the same
 * subgraph twice. First occurrence wins, so ordering is stable. */
export function dedupeGraphCitations(
  citations: readonly TicketMessageCitation[],
): Extract<TicketMessageCitation, { graphNodeId: string }>[] {
  const seen = new Set<string>();
  const out: Extract<TicketMessageCitation, { graphNodeId: string }>[] = [];
  for (const citation of citations) {
    if (!isGraphCitation(citation)) continue;
    if (seen.has(citation.graphNodeId)) continue;
    seen.add(citation.graphNodeId);
    out.push(citation);
  }
  return out;
}

/** True when the graph found the same claim recorded with DIFFERENT verdicts —
 * the single most useful thing it knows, and the reason a chip should stand out
 * rather than read as one more source. */
export function hasConflictingVerdicts(
  citation: Extract<TicketMessageCitation, { graphNodeId: string }>,
): boolean {
  return (citation.conflictingVerdicts?.length ?? 0) > 1;
}

/**
 * KDB-rule citations to render as chips: keep only the rule variant and collapse
 * ones that would render identically. The chip label is the rule's plain-English
 * `statement` (falling back to its raw `quote`), so two citations that would show
 * the same label — several rules synthesized from one source line, or one rule
 * cited twice — render as duplicate links. Dedupe on the visible label (statement
 * or quote, trimmed + case-insensitive), falling back to `ruleId`. First
 * occurrence wins, so ordering is stable.
 */
export function dedupeRuleCitations(
  citations: readonly TicketMessageCitation[],
): Extract<TicketMessageCitation, { ruleId: string }>[] {
  const seen = new Set<string>();
  const out: Extract<TicketMessageCitation, { ruleId: string }>[] = [];
  for (const citation of citations) {
    if (!isKnowledgeRuleCitation(citation)) continue;
    const label = citation.statement?.trim() || citation.quote?.trim();
    // Brand is part of the identity, not decoration: the same claim wording is
    // registered for several brands with DIFFERENT verdicts ("Made with only
    // milk and live cultures" is approved for Activia and conditional for
    // Oykos plain on the live Danone corpus). Keying on the label alone
    // collapsed those into one chip, so a reader asking about Activia never
    // saw that another brand's rule had been cited too. Rules that share a
    // label AND a brand still collapse — that is the duplicate this exists to
    // hide.
    const brand = citation.brand?.trim().toLowerCase() ?? "";
    const key = label
      ? `${label.toLowerCase()}|${brand}`
      : `rule:${citation.ruleId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(citation);
  }
  return out;
}

export function buildContextFileCitation(
  asset: ContextAssetRef,
  pin: ContextSourcePin,
): FileCitation {
  const citation: FileCitation = {
    url: pin.sourceUrl || asset.source,
    pageNumber: pin.page,
  };
  if (pin.originalURL) citation.originalURL = pin.originalURL;
  if (pin.relX != null && pin.relY != null) {
    citation.rel_x = pin.relX;
    citation.rel_y = pin.relY;
  }
  if (pin.videoStart != null) citation.videoStart = pin.videoStart;
  if (pin.cellAnchor) citation.cellAnchor = pin.cellAnchor;
  return citation;
}

export function mergeCitations(
  existing: TicketMessageCitation[] | undefined,
  added: TicketMessageCitation[],
): TicketMessageCitation[] {
  return [...(existing ?? []), ...added];
}

export function removeCitationAt(
  citations: TicketMessageCitation[],
  index: number,
): TicketMessageCitation[] {
  return citations.filter((_, i) => i !== index);
}

function truncate(text: string, maxLen = 48): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen - 1)}…`;
}

export type ReviewCommentSourceSummaryInput = {
  citations?: TicketMessageCitation[];
  cellAnchor?: CitationCellAnchor;
};

/** Header label for Ask V2 / review comment source blocks.
 *
 * why the rule/KDB branch runs FIRST: both variants can carry a `text` key, so
 * before `SPECIFIC_CITATION_KEYS` narrowed `isTextCitation` they were labelled
 * with that raw excerpt here. Narrowing the predicate without this branch would
 * have dropped them past every test onto `cellAnchor?.sheetName` or a bare
 * "Source" — the header would lose its label for exactly the citation type the
 * rest of this file exists to render. `citationLabel` gives a rule its
 * plain-English `statement`, which is the same label its chip shows, so the
 * header and the chip agree.
 *
 * Graph and agent-source citations deliberately have NO branch: `citationLabel`
 * has no name for either, so a branch could only return "Source" and would rob
 * the comment-level `cellAnchor` fall-through below of its turn. They skip to
 * that fall-through on purpose. */
export function reviewCommentSourceSummaryLabel(
  comment: ReviewCommentSourceSummaryInput,
): string {
  for (const citation of comment.citations ?? []) {
    if (isKnowledgeRuleCitation(citation) || isKnowledgeDbCitation(citation)) {
      return citationLabel(citation);
    }
    if (isTextCitation(citation)) {
      return citationLabel(citation);
    }
    if (isFileCitation(citation)) {
      const label = citationLabel(citation);
      // why: url-only file citations label as "Source"; comment-level cellAnchor
      // carries the sheet name the review UI should show.
      if (label !== "Source") {
        return label;
      }
      continue;
    }
  }
  if (comment.cellAnchor?.sheetName) return comment.cellAnchor.sheetName;
  return "Source";
}

export function citationLabel(citation: TicketMessageCitation): string {
  if (isKnowledgeRuleCitation(citation)) {
    const label = citation.statement?.trim() || citation.quote?.trim();
    return label ? truncate(label) : "Knowledge Database rule";
  }
  if (isKnowledgeDbCitation(citation)) return "Knowledge DB";
  if (isTextCitation(citation)) {
    // Fall back the way the rule branch above does. A guideline citation whose
    // excerpt is empty used to label its chip with the empty string, so the
    // reader got an underline and an icon with no word between them -- and a
    // screen reader got a link with no accessible name at all.
    const label = citation.text?.trim();
    return label ? truncate(label) : "Guideline";
  }
  if (isFileCitation(citation)) {
    if (citation.videoStart != null) {
      return formatTimeStamp(citation.videoStart);
    }
    if (citation.cellAnchor != null) {
      return cellAnchorLabel(citation.cellAnchor);
    }
    return "Source";
  }
  return "Source";
}

export function fileCitationToTarget(citation: {
  url?: string;
  pageNumber?: number;
  originalURL?: string;
  rel_x?: number;
  rel_y?: number;
  videoStart?: number;
  cellAnchor?: CitationCellAnchor;
}): CitationTarget {
  const page = citation.pageNumber ?? 1;
  const pos =
    citation.rel_x != null && citation.rel_y != null
      ? { x: citation.rel_x, y: citation.rel_y }
      : undefined;
  return {
    pageNumber: page,
    position: pos,
    sourceUrl: citation.url,
    originalURL: citation.originalURL,
    videoStart: citation.videoStart,
    cellAnchor: citation.cellAnchor,
  };
}

/** File citations first, then knowledge-DB records (text citations are not navigable). */
export function getNavigableCitations(
  comment: BaseTicketComment,
): TicketMessageCitation[] {
  const citations = Array.isArray(comment.citations) ? comment.citations : [];
  const fileCitations = citations.filter(isNavigableFileCitation);
  const kbCitations = citations.filter(isKnowledgeDbCitation);
  return [...fileCitations, ...kbCitations];
}

export function citationToTarget(
  citation: TicketMessageCitation,
): CitationTarget | null {
  if (isKnowledgeDbCitation(citation)) {
    return { knowledgeDataRecordId: citation.knowledgeDataRecordId };
  }
  if (isNavigableFileCitation(citation)) {
    return fileCitationToTarget(citation);
  }
  return null;
}
